/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import * as moment from "moment";
import { uniq, flatten, values } from "lodash";
import { Container } from "semantic-ui-react";

import { AnsweredQuestion, ConsultationDetails } from "@teamprevent/common/model";
import { updateQuestion, submitInvalidQuestionnaire, questionnaireLoaded } from "@teamprevent/common/questionnaires/actions";
import * as PhysiotherapyReferralReportExports from "@teamprevent/common/physiotherapyReferralReport/components/PhysiotherapyReferralReportV3";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { ConsultationDetailsState } from "@teamprevent/common/consultations/models";
import { TextSnippetSelector } from "@teamprevent/common/components/TextSnippetSelector";
import { usePrevious } from "@teamprevent/common/customHooks";
import { ExtraSection } from "@teamprevent/common/reportsCommon/reportsCommon";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";

import { PhysiotherapyReferralReport as PhysiotherapyReferralReportCommon  }
    from "@teamprevent/common/physiotherapyReferralReport/components/PhysiotherapyReferralReportV3";

import { PHYSIOTHERAPY_REFERRAL_REPORT_KEY, PhysiotherapyReferralReportDetails } from "@teamprevent/common/physiotherapyReferralReport/models";

import { ApplicationState } from "../../applicationState";
import { OhCaseState, selectors as ohCaseSelectors } from "../../crud/ohCase";
import { routeIdSelector as ohCaseIdSelector } from "../../crud/ohCase/selectors";
import { EmployeeRecordInRedux, EmployeeRecordState } from "../../crud/employeeRecord";
import { loadEmployeeRecordDetail } from "../../crud/employeeRecord/actions";
import {
    savePhysiotherapyReferralReport,
    loadConsultationAndPhysiotherapyReferralDetails,
    submitPhysiotherapyReferralReport,
    loadQuestionnaire as loadQuestionnaireAction
} from "../actions";
import { ActionableTasksState } from "../../actionableTasks";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { AddTaskFormModel, CreateTaskDto, TaskType, mapAddTaskFormModelToCreateTaskDto } from "../../task/model";
import { customerSelector } from "../../crud/customer/selectors";
import { CurrentUser } from "../../auth";

import { replace } from "redux-little-router";

interface AdminReferralReportOwnProps {
    nextTaskRequired?: boolean;
    oldVersionRedirectUrl?: string;
    version: number;
    isReportPreview?: boolean;
    submitReport?: (ohCaseId: string, taskId: string, tasks?: CreateTaskDto[]) => Promise<any>;
}

export interface AdminReferralReportStateProps {
    customerId: string;
    ohCaseId: string;
    taskId: string;
    task?: TaskModel;
    ohCase: { employeeRecordId: string } | null;
    employeeRecord: EmployeeRecordInRedux | null;
    report: PhysiotherapyReferralReportExports.ReportModel;
    readonly: boolean;
    consultationAndReferralDetails: ConsultationDetails;
    loading?: boolean;
    useToyotaTemplates: boolean;
    successorTasks: TaskType[];
    currentUser: CurrentUser;
}

interface AdminReferralReportDispatchProps {
    loadEmployee: (employeeRecordId: string, customerId: string) => void;
    updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }, questionnaireKey: string) => void;
    saveReport: (ohCaseId: string, taskId: string, report: PhysiotherapyReferralReportExports.ReportModel) => Promise<void>;
    loadQuestionnaire: (
        questionnaireKey: string, ohCaseId: string, taskId: string, omittedSections?: string[]
    ) => Promise<PhysiotherapyReferralReportDetails>;
    clearQuestionnaire: (questionnaireKey: string) => void;
    submitInvalidQuestionnaire: (questionnaireKey: string) => void;
    onCompletion: () => void;
    loadConsultationDetails: (ohCaseId: string, customerId: string) => void;
    submitReport: (ohCaseId: string, taskId: string, tasks?: CreateTaskDto[]) => Promise<any>;
    navigateToOldReport: (url: string) => void;
}

interface AdditionalTask {
    task: AddTaskFormModel;
    isValid: boolean;
}

type Props = AdminReferralReportOwnProps & AdminReferralReportStateProps & (
    ({ readonly: true } & Omit<AdminReferralReportDispatchProps, "saveReport">) |
    ({ readonly: false } & AdminReferralReportDispatchProps)
);

const reportDateQuestion = { questionText: "Date", id: "Date" };
const reportDetailsSectionId = "ReportDetails";

const PhysiotherapyReferralReportUnconnected: React.FunctionComponent<Props> = (props: Props) => {

    const updateAssessmentAndOpinionsQuestion = (value: any) => {
        const assessmentAndOpinionsQuestion = props.report.questions.find(q => q.id === "AssessmentAndOpinions");
        if (assessmentAndOpinionsQuestion) {
            props.updateAnswer({
                sectionId: assessmentAndOpinionsQuestion.sectionId,
                id: assessmentAndOpinionsQuestion.id,
                question: {
                    ...assessmentAndOpinionsQuestion,
                    answer: (assessmentAndOpinionsQuestion.answer || "") + value
                }
            }, PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
        }
    };

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [tasks, setTasks] = React.useState<AdditionalTask[]>(
        props.nextTaskRequired ? [{ task: { taskType: null, taskCode: null, dueDate: null, notes: null }, isValid: false }] : null
    );
    const [extraSections, setExtraSections] = React.useState<ExtraSection[]>(
        !props.readonly && props.successorTasks && props.nextTaskRequired ? [{
            id: "NextTask",
            title: "Next Task",
            content: (
                <AddTasksInputs
                    taskTypes={props.successorTasks}
                    onChange={setTasks}
                    ohCaseId={props.ohCaseId}
                    showErrors={submitted}
                    assignmentOptionRequired={true}
                />)
        }] : []
    );
    const [extraQuestionContent, setExtraQuestionContent] = React.useState<{ [questionId: string]: React.ReactNode }>(
        props.readonly ? null : {
            AssessmentAndOpinions: (
                <TextSnippetSelector
                    onSelect={updateAssessmentAndOpinionsQuestion}
                />)
        }
    );
    const [reportDetails, setReportDetails] = React.useState<PhysiotherapyReferralReportDetails | null>(null);

    const prevProps = usePrevious(props);

    React.useEffect(() => {
        if (props.currentUser && props.currentUser !== prevProps?.currentUser) {
            updateCurrentUserAnswers();
        }

        if (props.readonly !== prevProps?.readonly
            || props.successorTasks !== prevProps?.successorTasks
            || props.nextTaskRequired !== prevProps?.nextTaskRequired
            || submitted) {
            const newExtraSections = !props.readonly &&
                props.successorTasks &&
                props.nextTaskRequired ? [{
                    id: "NextTask",
                    title: "Next Task",
                    content: (
                        <AddTasksInputs
                            taskTypes={props.successorTasks}
                            onChange={setTasks}
                            ohCaseId={props.ohCaseId}
                            showErrors={submitted}
                            assignmentOptionRequired={true}
                        />)
                }
                ] : [];

            setExtraSections(newExtraSections);
        }

        if (props.readonly !== prevProps?.readonly) {
            const newExtraQuestionContent = props.readonly ? null : {
                AssessmentAndOpinions: (
                    <TextSnippetSelector
                        onSelect={updateAssessmentAndOpinionsQuestion}
                    />)
            };

            setExtraQuestionContent(newExtraQuestionContent);
        }
    }, [props, prevProps, submitted]);

    const loadConsultationDetails = async (ohCaseId: string, taskId: string) => {
        await props.loadConsultationDetails(ohCaseId, taskId);
    };

    const loadQuestionnaire = async (questionnaireKey: string, ohCaseId: string, taskId: string, customerId: string, omittedSections?: string[]) => {
        const report = await props.loadQuestionnaire(questionnaireKey, ohCaseId, taskId, omittedSections);
        setReportDetails(report);

        if (props.currentUser) {
            updateCurrentUserAnswers();
        }
        return report;
    };

    const submitQuestionnaire = async (report: PhysiotherapyReferralReportExports.ReportModel): Promise<boolean> => {
        if (props.readonly === true) { return false; }
        setSubmitted(true);

        let valid = true;

        if (!report.questions.every(question => question.isValid)) {
            props.submitInvalidQuestionnaire(PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
            valid = false;
        }

        if (props.nextTaskRequired && tasks.some(t => !t.isValid)) {
            valid = false;
        }

        if (valid) {
            const dateAnswerIndex = report.questions.findIndex(q => q.sectionId === reportDetailsSectionId && q.id === reportDateQuestion.id);
            const dateAnswer = {
                id: reportDateQuestion.id,
                question: reportDateQuestion.questionText,
                answer: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
                isValid: true,
                sectionId: reportDetailsSectionId
            };

            if (dateAnswerIndex > -1) {
                report.questions[dateAnswerIndex] = dateAnswer;
            } else {
                report.questions.push(dateAnswer);
            }

            await props.saveReport(props.ohCaseId, props.taskId, report);

            const createTasks = props.nextTaskRequired ? tasks.map(t => mapAddTaskFormModelToCreateTaskDto(t.task, props.ohCaseId)) : undefined;

            await props.submitReport(props.ohCaseId, props.taskId, createTasks);

            props.onCompletion();

            return true;
        }

        return false;
    };

    const updateCurrentUserAnswers = () => {
        if (!props.readonly) {
            const clinicianNameQuestion = PhysiotherapyReferralReportExports.clinicianNameQuestion;
            const clinicianQualificationQuestion = PhysiotherapyReferralReportExports.clinicianQualificationQuestion;
            const clinicianEmailQuestion = PhysiotherapyReferralReportExports.clinicianEmailQuestion;

            props.updateAnswer({
                sectionId: reportDetailsSectionId,
                id: "CliniciansId",
                question: { question: clinicianNameQuestion.questionText, answer: props.currentUser.id, isValid: true }
            }, PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
            props.updateAnswer({
                sectionId: reportDetailsSectionId,
                id: clinicianNameQuestion.id,
                question: {
                    question: clinicianNameQuestion.questionText,
                    answer: props.currentUser.name,
                    isValid: true
                }
            }, PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
            props.updateAnswer({
                sectionId: reportDetailsSectionId,
                id: clinicianQualificationQuestion.id,
                question: { question: clinicianQualificationQuestion.questionText, answer: props.currentUser.qualification, isValid: true }
            }, PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
            props.updateAnswer({
                sectionId: reportDetailsSectionId,
                id: clinicianEmailQuestion.id,
                question: { question: clinicianEmailQuestion.questionText, answer: props.currentUser.email, isValid: true }
            }, PHYSIOTHERAPY_REFERRAL_REPORT_KEY);
        }
    };

    return (
        <Container className="referral-report">
            {!props.readonly && props.employeeRecord &&
                <h2 className="sticky-header">Referral Report for {props.employeeRecord.firstName} {props.employeeRecord.lastName}</h2>}
            <PhysiotherapyReferralReportCommon
                {...props}
                fromAdmin
                isReportPreview={props.isReportPreview}
                loadQuestionnaire={loadQuestionnaire}
                loadConsultationDetails={loadConsultationDetails}
                submitQuestionnaire={submitQuestionnaire}
                extraSections={extraSections}
                extraQuestionContent={extraQuestionContent}
                navigateToOldReport={(url: string) => props.navigateToOldReport(url)}
                version={props.version}
                redirectData={{
                    ohCaseId: props.ohCaseId,
                    customerId: props.customerId,
                    employeeRecordId: props.employeeRecord?.id,
                    taskId: props.taskId
                }}
                reportDetails={reportDetails}
            />
        </Container>
    );
};

const mapStateToProps = (
    state: ApplicationState & OhCaseState & EmployeeRecordState & ConsultationDetailsState & ActionableTasksState, ownProps: AdminReferralReportOwnProps):
   AdminReferralReportStateProps => {
    const taskId = state.router.params?.taskId;
    let task = state.actionableTasks.tasks.find(t => t.id === taskId);
    const ohCaseId = ohCaseIdSelector(state) || (task && task.caseId);
    const ohCase = ohCaseSelectors.ohCase(state);
    let employeeRecord;
    if (ohCase && "tasks" in ohCase && ohCase.tasks) {
        task = task || ohCase.tasks.find(t => t.id === taskId);
        employeeRecord = state.employeeRecordsDetail.employeeRecords.find(e => e.id === ohCase.employeeRecordId);
    }
    const consultationAndReferralDetails = state.consultationDetails.find(r => r.referralReportTaskId === taskId);

    const questions = uniq(flatten(state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.sections.map(section => values(section.questions))));

    return {
        taskId,
        task,
        ohCaseId,
        customerId: state.router.params?.customerId || task?.customer.id,
        currentUser: state.currentUser,
        ohCase: ohCase as { employeeRecordId: string }, // The typings for ohcase are not quite right so forced to cast here
        report: {
            questions,
            eTag: state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.eTag,
            isDefaultCustomerType: state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.isDefaultCustomerType,
            customerType: state.questionnaires[PHYSIOTHERAPY_REFERRAL_REPORT_KEY]?.customerType
        },
        useToyotaTemplates: customerSelector(state).useToyotaTemplates,
        readonly: !task || task.completed || ownProps.isReportPreview,
        employeeRecord,
        consultationAndReferralDetails,
        successorTasks: consultationAndReferralDetails?.successorTasks,
        loading: !consultationAndReferralDetails || !employeeRecord || !task
    };
};

const mapDispatchToProps = (dispatch: any, ownProps: AdminReferralReportOwnProps): AdminReferralReportDispatchProps => {
    return {
        clearQuestionnaire: (questionnaireKey: string) => dispatch(questionnaireLoaded({
            questions: [],
            questionnaireKey,
            isDefaultCustomerType: true,
            customerType: undefined
        })),
        loadQuestionnaire: (questionnaireKey: string, ohCaseId: string, taskId: string, omittedSections?: string[]) =>
            dispatch(loadQuestionnaireAction(ohCaseId, taskId, questionnaireKey, omittedSections)),
        updateAnswer: (payload: { sectionId: string; id: string; question: AnsweredQuestion }, questionnaireKey: string) =>
            dispatch(updateQuestion({ ...payload, questionnaireKey })),
        loadEmployee: (employeeRecordId: string, customerId: string) => dispatch(loadEmployeeRecordDetail({ employeeRecordId, customerId })),
        saveReport: (ohCaseId: string, taskId: string, report: PhysiotherapyReferralReportExports.ReportModel) =>
            dispatch(savePhysiotherapyReferralReport(ohCaseId, taskId, report.questions, report.eTag)),
        submitReport: async (ohCaseId: string, taskId: string, tasks?: CreateTaskDto[]) =>
        {
            if (ownProps.submitReport)
            {
                await ownProps.submitReport(ohCaseId, taskId, tasks ?? []);
            }
            else
            {
                await dispatch(submitPhysiotherapyReferralReport(ohCaseId, taskId, tasks ?? []));
            }
        },
        onCompletion: () => dispatch(navigateToParentRoute()),
        submitInvalidQuestionnaire: (questionnaireKey: string) => dispatch(submitInvalidQuestionnaire(questionnaireKey)),
        loadConsultationDetails: (ohCaseId: string, taskId: string) => dispatch(loadConsultationAndPhysiotherapyReferralDetails(ohCaseId, taskId)),
        navigateToOldReport: (url: string) => dispatch(replace(url))
    };
};

export const PhysiotherapyReferralReport = connect(mapStateToProps, mapDispatchToProps)(PhysiotherapyReferralReportUnconnected);
